// ** React Imports
import React, { createContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux';

// ** Constants Imports
import { SAVED_VIEW_TYPE, VIEW_TYPE } from '../../../../../constants/reportView';
import { API_INTELLIGENCE_LLM_MODELS, CLIENT_REPORT_VIEW, DEFAULT_DATE_FORMAT } from '../../../../../constants';

// ** Components Imports
import ViewReportCard from './viewReportCard';
import Confirm from '../report-view/dialog/Confirm';
import ShareURL from '../report-view/dialog/ShareURL';
import CreateNewView from '../report-view/dialog/CreateNewView';
import Headline from '../../../../general/Headline';
import { callCompressTokenApi, callTokenApi } from '../../../../../apiCaller';
import { fetchSeverSideTriggerRequest, setSavedReport } from '../../../../../actions/subscriber';
import ManageSchedule from './scheduled-deliveries/manageSchedule';
import ManageTemplate from './ai-insights/manageTemplate';

import { useLocation } from 'react-router';
import { decodeObj } from '../../../../../utils';
import VariableInRules from '../../../../general/popup/VariableInRules';
import cloneDeep from 'lodash/cloneDeep';
import CenterSpinner from '../../../../general/Loadings/CenterSpinner';

export const ScheduleContext = createContext();

const SavedReports = () => {
     const dispatch = useDispatch();
     const user = useSelector((state) => state.theme.user);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const savedReports = useSelector((state) => state.subscriber.savedReport);
     const location = useLocation();

     const scheduleParam = new URLSearchParams(location.search).get('scheduleSaveReport');

     const parseParams = decodeObj(scheduleParam);
     const scheduleReportId = parseParams?.id;
     const reportViewId = parseParams?.reportViewId;

     // ** State
     const [show, setShow] = useState(false);
     const [data, setData] = useState({});
     const [viewSelect, setViewSelect] = useState({});
     const [showSchedule, setShowSchedule] = useState(false);
     const [showAiTemplate, setShowAiTemplate] = useState(false);
     const [reportData, setReportData] = useState(null);
     const [variableInRules, setVariableInRules] = useState(false);
     const [models, setModels] = useState([]);
     const [isLoading, setIsLoading] = useState(false);     
     const [existTrigger, setExistTrigger] = useState(false);     //sever side triggers
     const listSeverSideTrigger = useSelector((state) => state.subscriber.listSeverSideTrigger);

     const fetchSeverSideTrigger = () => {
          if (!listSeverSideTrigger || listSeverSideTrigger.length === 0) {
               dispatch(fetchSeverSideTriggerRequest(activeAccount.id));
          }
     };
     useEffect(fetchSeverSideTrigger, [activeAccount.id, listSeverSideTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

     // ** Methods
     const handleConfirm = (_item, view) => {
          setShow(true);
          setData(_item);
          setViewSelect(view);
     };

     const handleSchedule = (item) => {
          setShowSchedule(true);
          setReportData(item);
     };

     const handleAiTemplate = (item) => {
          setShowAiTemplate(true);
          setReportData(item);
     };

     useEffect(() => {
          if (reportViewId && scheduleReportId) {
               const reportViewLink = savedReports.find((report) => report.id === reportViewId);
               handleSchedule(reportViewLink);
          }
     }, [parseParams]) // eslint-disable-line

     const contextValue = {
          showSchedule,
          setShowSchedule,
          showAiTemplate,
          setShowAiTemplate,
          reportData,
          setReportData,
          scheduleReportId,
          models,
     }

     const handleApiResponse = (response, type, savedReportId, successCallback) => {
          if (response.status === 200) {
               successCallback();

               let data = {};
               if (type === SAVED_VIEW_TYPE.DELETE) {
                    data = { id: savedReportId, deleted: true }
               } else {
                    data = response.data?.reportViews
               }
               dispatch(setSavedReport(data));
               toast.success(response.data.message);
          } else {
               if (response?.data?.isUsed) {
                    setVariableInRules(true);
                    return;
               }

               toast.error(response.data.message);
          }
     };

     const handleApiError = () => {
          setShow(false);
     };

     const onAccept = async (values, { setSubmitting }) => {
          if (viewSelect.id) {
               let apiCall;
               let payload;
               if(data?.type === SAVED_VIEW_TYPE.DELETE && listSeverSideTrigger.length > 0 && listSeverSideTrigger.some((trigger) => trigger?.schedule?.reportViewId === viewSelect.id)) {
                    setExistTrigger(true);
                    setShow(false)
                    return
               } 
               switch (data.type) {
                    case SAVED_VIEW_TYPE.DELETE:
                         const query = `?id=${viewSelect.id}&accountId=${activeAccount.accountId}&userId=${user.id}&reportName=${viewSelect.reportName}`;
                         apiCall = callTokenApi(`${CLIENT_REPORT_VIEW}/${query}`, 'DELETE');
                         payload = null;
                         break;

                    case SAVED_VIEW_TYPE.SETTING:
                         const dataDateRangeConfig = values.dateRangeConfig
                         if (dataDateRangeConfig?.dateRanges) {

                              if (dataDateRangeConfig.dateRanges.selection) {
                                   dataDateRangeConfig.dateRanges.selection = {
                                        startDate: dataDateRangeConfig.dateRanges.selection.startDate.format(DEFAULT_DATE_FORMAT),
                                        endDate: dataDateRangeConfig.dateRanges.selection.endDate.format(DEFAULT_DATE_FORMAT),
                                   };
                              }

                              if (dataDateRangeConfig.dateRanges.compare) {
                                   dataDateRangeConfig.dateRanges.compare = {
                                        startDate: dataDateRangeConfig.dateRanges.compare.startDate.format(DEFAULT_DATE_FORMAT),
                                        endDate: dataDateRangeConfig.dateRanges.compare.endDate.format(DEFAULT_DATE_FORMAT),
                                   };
                              }

                         }
                         if (dataDateRangeConfig?.rolling?.customDateCompare) {
                              if (dataDateRangeConfig.rolling.customDateCompare.selection) {

                                   dataDateRangeConfig.rolling.customDateCompare.selection = {
                                        startDate: dataDateRangeConfig.rolling.customDateCompare.selection.startDate.format(DEFAULT_DATE_FORMAT),
                                        endDate: dataDateRangeConfig.rolling.customDateCompare.selection.endDate.format(DEFAULT_DATE_FORMAT),
                                   };

                              }
                         }

                         const content = cloneDeep(viewSelect.content);
                         content.dateRangeConfig = dataDateRangeConfig

                         payload = {
                              id: viewSelect.id,
                              accountId: activeAccount.accountId,
                              name: values.name,
                              description: values.description,
                              isAvailable: values.isAvailable,
                              content: JSON.stringify(content),
                              isReport: true,
                         };

                         apiCall = callCompressTokenApi(CLIENT_REPORT_VIEW, 'PUT', payload);

                         break;

                    default:
                         return;
               }

               try {                    
                    const response = await apiCall;
                    handleApiResponse(response, data.type, viewSelect.id, () => { });
               } catch (error) {
                    toast.error('An error occurred while processing your request.');
               } finally {
                    setSubmitting(false);
                    handleApiError();
               }
          }
     };

     useEffect(() => {
          const loadData = async () => {
               setIsLoading(true);
               try {
                    // Load models data
                    const modelsResponse = await callTokenApi(API_INTELLIGENCE_LLM_MODELS, 'GET');
                    const modelsData = modelsResponse?.data?.data;
                    if (modelsResponse?.status === 200 && modelsData) {
                         setModels(modelsData);
                    } else {
                         toast.error('Failed to load models data.');
                    }

               } catch (error) {
                    console.error('Fetch data error:', error);
                    toast.error('An error occurred while loading data.');
               } finally {
                    setIsLoading(false);
               }
          };

          loadData();
     }, []);
               
     return (
          isLoading ? <CenterSpinner /> : 
          <>
               <div className="custom-datalayer-variables">
                    <div className="d-flex justify-content-between">
                         <div className="col-8 pl-0">
                              <Headline title="Save Reports">
                                   <p>Build and manage save reports</p>
                              </Headline>
                         </div>
                    </div>
                    <div className="accordion list-custom-reports" style={{ marginBottom: '40px' }}>
                         <div className="accordion-item" style={{ border: 0 }}>
                              <div className="list-rules">
                                   {savedReports.map((viewReport, index) => (
                                        <ViewReportCard
                                             key={index}
                                             index={index}
                                             viewReport={viewReport}
                                             handleConfirm={handleConfirm}
                                             handleSchedule={handleSchedule}
                                             handleAiTemplate={handleAiTemplate}
                                        />
                                   ))}
                              </div>
                         </div>
                    </div>
               </div>

               {(data.type === SAVED_VIEW_TYPE.SET_DEFAULT || data.type === SAVED_VIEW_TYPE.DELETE) && (
                    <Confirm show={show} data={data} onCancel={() => setShow(false)} onAccept={onAccept} />
               )}
               {data.type === SAVED_VIEW_TYPE.SETTING && (
                    <CreateNewView show={show} type={VIEW_TYPE.REPORT} data={viewSelect} onAccept={onAccept} onCancel={() => setShow(false)} />
               )}
               {data.type === SAVED_VIEW_TYPE.SHARE_URL && (
                    <ShareURL link={viewSelect.shareLink} show={show} data={data} onCancel={() => setShow(false)} />
               )}
               {reportData && (
                    <ScheduleContext.Provider value={contextValue}>
                         <ManageSchedule savedReports={savedReports} />
                         <ManageTemplate />
                    </ScheduleContext.Provider>
               )}
               <VariableInRules show={variableInRules} onClose={() => setVariableInRules(false)}>
                    {data?.type === SAVED_VIEW_TYPE.SETTING ? (
                         <p>
                              This report is used in the AI Insight. You must remove it from all existing template before you can change date range
                              option for it.
                         </p>
                    ) : (
                         <p>This report is used in the AI Insight. You must remove it from all existing template before you can delete it.</p>
                    )}
               </VariableInRules>
               <VariableInRules show={existTrigger} onClose={() => setExistTrigger(false)}>
                    <p>This report is used in Server Side Trigger. You must remove it from all existing template before you can delete it.</p>
               </VariableInRules>
          </>
     );
};

export default SavedReports;
